import React, { useState } from 'react';
import api from '../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { BookOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Bartender / manager / director-only recipe viewer.
 * Strips menu recipes from customer payloads — fetches them on demand.
 */
export default function RecipePopover({ itemId, itemName }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recipe, setRecipe] = useState(null);

  const handleOpen = async (next) => {
    setOpen(next);
    if (next && !recipe) {
      setLoading(true);
      try {
        const { data } = await api.get(`/menu/recipe/${itemId}`);
        setRecipe(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load recipe');
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-accent/80 hover:text-blue-accent border border-blue-accent/30 hover:border-blue-accent/60 rounded-sm px-2 py-1 transition-colors font-sans"
          data-testid={`recipe-toggle-${itemId}`}
          aria-label={`View recipe for ${itemName}`}
        >
          <BookOpen className="w-3 h-3" /> Recipe
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-72 bg-bg-secondary border border-blue-accent/20 text-white p-3"
        data-testid={`recipe-content-${itemId}`}
      >
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-white/5">
          <p className="font-serif text-sm text-gold">{itemName}</p>
          <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-sans">Staff only</span>
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-zinc-400 text-xs font-sans py-3">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        )}
        {!loading && recipe && (
          recipe.recipe && recipe.recipe.length > 0 ? (
            <ul className="space-y-1.5 font-sans">
              {recipe.recipe.map((row, idx) => (
                <li
                  key={`${row.ingredient}-${idx}`}
                  className="flex items-baseline justify-between text-xs"
                >
                  <span className="text-zinc-300">{row.ingredient}</span>
                  <span className="text-blue-accent font-mono ml-3">{row.amount}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-zinc-500 font-sans py-2">No recipe recorded for this item.</p>
          )
        )}
      </PopoverContent>
    </Popover>
  );
}
